import { useMemo, useState } from 'react'
import { DashboardLayout } from '../../components/layout'
import { Button, Modal, StatCard } from '../../components/ui'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import { useExpenses, useProducts } from '../../hooks'
import { formatCurrency } from '../../lib/format'
import { useToast } from '../../context/ToastContext'
import ExpenseForm from '../../features/operations/ExpenseForm'
import ExpenseTable from '../../features/operations/ExpenseTable'
import ExpenseFilters from '../../features/operations/ExpenseFilters'

const categories = ['Infrastructure', 'Software', 'Marketing', 'Legal', 'Payroll', 'Other']

function getQuarter(date) {
  const month = date.getMonth() + 1
  return Math.ceil(month / 3)
}

function ExpensesPage() {
  const { products, loading: productsLoading } = useProducts()
  const { expenses, loading: expensesLoading, createExpense } = useExpenses()
  const [showAddModal, setShowAddModal] = useState(false)
  const [formLoading, setFormLoading] = useState(false)
  const [csvLoading, setCsvLoading] = useState(false)
  const [filters, setFilters] = useState({
    productId: '',
    category: '',
    startDate: '',
    endDate: '',
  })
  const toast = useToast()

  const filteredExpenses = useMemo(() => {
    return expenses.filter(expense => {
      if (filters.productId && expense.product_id !== filters.productId) return false
      if (filters.category && expense.category !== filters.category) return false
      if (filters.startDate && expense.date < filters.startDate) return false
      if (filters.endDate && expense.date > filters.endDate) return false
      return true
    })
  }, [expenses, filters])

  const totals = useMemo(() => {
    const now = new Date()
    const currentMonth = now.getMonth()
    const currentQuarter = getQuarter(now)

    const monthTotal = filteredExpenses.reduce((sum, expense) => {
      const expenseDate = new Date(expense.date)
      return expenseDate.getMonth() === currentMonth ? sum + Number(expense.amount || 0) : sum
    }, 0)

    const quarterTotal = filteredExpenses.reduce((sum, expense) => {
      const expenseDate = new Date(expense.date)
      return getQuarter(expenseDate) === currentQuarter ? sum + Number(expense.amount || 0) : sum
    }, 0)

    const totalExpenses = filteredExpenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0)
    const revenuePlaceholder = 0

    return {
      monthTotal,
      quarterTotal,
      totalExpenses,
      revenuePlaceholder,
      pnl: revenuePlaceholder - totalExpenses,
    }
  }, [filteredExpenses])

  const handleFilterChange = (e) => {
    const { name, value } = e.target
    setFilters(prev => ({ ...prev, [name]: value }))
  }

  const handleCreateExpense = async (formData) => {
    setFormLoading(true)
    const { error } = await createExpense(formData)
    setFormLoading(false)

    if (error) {
      toast.error('Failed to add expense')
      return
    }

    toast.success('Expense added')
    setShowAddModal(false)
  }

  const handleCsvImport = async (file) => {
    if (!file) return
    setCsvLoading(true)

    try {
      const text = await file.text()
      const lines = text.trim().split(/\r?\n/)
      if (lines.length < 2) throw new Error('CSV needs a header and at least one row')

      const headers = lines[0].split(',').map(h => h.trim().toLowerCase())
      const rows = lines.slice(1)

      for (const row of rows) {
        if (!row.trim()) continue
        const values = row.split(',').map(value => value.trim())
        const rowData = headers.reduce((acc, header, index) => {
          acc[header] = values[index]
          return acc
        }, {})

        const productMatch = products.find(p => p.name.toLowerCase() === (rowData.product || '').toLowerCase())
        if (!productMatch) {
          throw new Error(`Product not found for row: ${row}`)
        }

        await createExpense({
          date: rowData.date,
          amount: Number(rowData.amount),
          category: rowData.category,
          product_id: productMatch.id,
          vendor: rowData.vendor || null,
          notes: rowData.notes || null,
        })
      }

      toast.success('CSV imported successfully')
    } catch (err) {
      toast.error(err.message || 'Failed to import CSV')
    }

    setCsvLoading(false)
  }

  if (productsLoading || expensesLoading) {
    return (
      <DashboardLayout title="Expenses">
        <LoadingSpinner.Overlay message="Loading expenses..." />
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout
      title="Expenses"
      actions={
        <div className="row gap-sm">
          <label className="btn btn-secondary" htmlFor="csvImport">
            {csvLoading ? 'Importing...' : 'Import CSV'}
          </label>
          <input
            id="csvImport"
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={(e) => handleCsvImport(e.target.files?.[0])}
          />
          <Button onClick={() => setShowAddModal(true)}>+ Add Expense</Button>
        </div>
      }
    >
      <div className="grid grid-4 mb-8">
        <StatCard label="This Month" value={formatCurrency(totals.monthTotal)} />
        <StatCard label="This Quarter" value={formatCurrency(totals.quarterTotal)} />
        <StatCard label="Total Expenses" value={formatCurrency(totals.totalExpenses)} changeType="negative" />
        <StatCard label="P&L (placeholder)" value={formatCurrency(totals.pnl)} changeType={totals.pnl >= 0 ? 'positive' : 'negative'} />
      </div>

      <div className="card mb-6">
        <h3 className="text-lg font-bold mb-4">Filters</h3>
        <ExpenseFilters
          products={products}
          categories={categories}
          filters={filters}
          onChange={handleFilterChange}
        />
      </div>

      <ExpenseTable expenses={filteredExpenses} />

      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Expense"
      >
        <ExpenseForm
          products={products}
          categories={categories}
          onSubmit={handleCreateExpense}
          onCancel={() => setShowAddModal(false)}
          loading={formLoading}
        />
      </Modal>
    </DashboardLayout>
  )
}

export default ExpensesPage
